import {
  CHARACTER_IDS,
  type CharacterDef,
  type CharacterId,
  type RacialDef,
  type RacialId,
} from '../types';

export const DEFAULT_CHARACTER: CharacterId = 'warlock';

/**
 * Character catalog. Each entry names its racial and any base-stat overrides; the hitbox is the
 * same for all. The sim reads these only through `characterMods`, so the `racialAbilities`
 * setting can turn every entry back into the baseline.
 */
export const CHARACTERS = {
  warlock: {
    id: 'warlock',
    displayName: 'Warlock',
    blurb: 'Exiled hedge-sorcerer who deals in fire and pacts.',
    modelKey: 'warlock',
    iconKey: 'warlock',
    enabled: true,
    racial: 'pactfire',
  },
  mage: {
    id: 'mage',
    displayName: 'Mage',
    blurb: 'Tower scholar of the arcane orders.',
    modelKey: 'mage',
    iconKey: 'mage',
    enabled: true,
    racial: 'arcaneRecall',
  },
  ranger: {
    id: 'ranger',
    displayName: 'Ranger',
    blurb: 'Hooded wilds-walker who binds spells into runes.',
    modelKey: 'ranger',
    iconKey: 'ranger',
    enabled: true,
    racial: 'fleetfoot',
  },
  graniteborn: {
    id: 'graniteborn',
    displayName: 'Graniteborn',
    blurb: 'Stone-skinned earth-caller who braces against the blow.',
    modelKey: 'graniteborn',
    iconKey: 'graniteborn',
    enabled: true,
    racial: 'stoneGuard',
    stats: { moveSpeed: 210 },
  },
  rogue: {
    id: 'rogue',
    displayName: 'Rogue',
    blurb: 'Hooded knife-for-hire who strikes from nowhere.',
    modelKey: 'rogue',
    iconKey: 'rogue',
    enabled: true,
    racial: 'stealth',
    stats: { maxHp: 95 },
  },
} as const satisfies Record<CharacterId, CharacterDef>;

export function isPickable(id: CharacterId): boolean {
  return (CHARACTERS as Record<CharacterId, CharacterDef>)[id].enabled;
}

/** Any input → a pickable id. Unknown, disabled or non-string values give DEFAULT_CHARACTER. */
export function resolveCharacter(raw: unknown): CharacterId {
  if (typeof raw !== 'string') return DEFAULT_CHARACTER;
  const id = CHARACTER_IDS.find((c) => c === raw);
  return id !== undefined && isPickable(id) ? id : DEFAULT_CHARACTER;
}

/** Racial catalog: every racial number lives here, as spell numbers live in `spells.ts`. */
export const RACIALS = {
  stoneGuard: {
    id: 'stoneGuard',
    name: 'Stone Guard',
    blurb: 'Brace toward the cursor: hits from the front deal half damage and 35% knockback.',
    kind: 'guard',
    cooldownMs: 15_000,
    durationMs: 750,
    arcDeg: 120,
    kbMult: 0.35,
    damageMult: 0.5,
    moveSpeedMult: 0.5,
  },
  pactfire: {
    id: 'pactfire',
    name: 'Pactfire',
    blurb: 'The first Fireball each round skips its cooldown.',
    kind: 'passive',
    freeCastsPerRound: { Q: 1 },
  },
  arcaneRecall: {
    id: 'arcaneRecall',
    name: 'Arcane Recall',
    blurb: 'Mobility (D) spell cooldown is 10% shorter.',
    kind: 'passive',
    cooldownMult: { D: 0.9 },
  },
  fleetfoot: {
    id: 'fleetfoot',
    name: 'Fleetfoot',
    blurb: 'Walks 5% faster on stone.',
    kind: 'passive',
    surface: { stoneSpeedMult: 1.05 },
  },
  stealth: {
    id: 'stealth',
    name: 'Stealth',
    blurb:
      'Space: vanish for 3 s. Enemies see nothing. Casting, taking a hit or touching lava reveals you. 18 s cooldown.',
    kind: 'active',
    effect: 'stealth',
    cooldownMs: 18_000,
    durationMs: 3_000,
  },
} as const satisfies Record<RacialId, RacialDef>;
